import {
  Button,
  Menu,
  ThemeIcon,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconGift,
  IconHelp,
  IconLogout,
  IconMoon,
  IconSettings,
  IconSun,
  IconUser,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { chatSupportLink, supportEmail } from "../../utils/noanLinks";
import { useFeatureAccess } from "../../utils/useFeatureAccess";
import { useSignOut } from "../../utils/useSignOut";
import { Can } from "../auth/Can";
import { ReferralModal } from "../ReferralModal";
import classes from "./Layout.module.css";

export function UserMenu({
  variant = "default",
  withReferral = false,
}: {
  variant?: "sidebar" | "default";
  withReferral?: boolean;
}) {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme("light", {
    getInitialValueInEffect: true,
  });
  const [
    referralModalOpened,
    { open: openReferralModal, close: closeReferralModal },
  ] = useDisclosure();

  const { signOut } = useSignOut();
  const { isRestrictedRole } = useFeatureAccess();

  return (
    <Menu shadow="sm" position="bottom-end">
      <Menu.Target>
        <Button
          color={variant === "sidebar" ? "dimmed" : undefined}
          size={variant === "sidebar" ? "sm" : undefined}
          variant="subtle"
          leftSection={
            <ThemeIcon
              variant="transparent"
              color={variant === "sidebar" ? "text" : undefined}
              size={"sm"}
            >
              <IconUser />
            </ThemeIcon>
          }
          classNames={{
            root: classes.accountButton,
            section: classes.accountButtonSection,
            label: classes.accountButtonLabel,
          }}
        >
          Account
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {!isRestrictedRole && (
          <Menu.Item
            component={Link}
            leftSection={
              <ThemeIcon variant="transparent" color="dark" size="xs">
                <IconSettings />
              </ThemeIcon>
            }
            to="/account"
          >
            Settings
          </Menu.Item>
        )}

        {withReferral && (
          <Can execute="referral.read">
            <Menu.Item
              leftSection={
                <ThemeIcon variant="transparent" color="dark" size="xs">
                  <IconGift />
                </ThemeIcon>
              }
              onClick={openReferralModal}
            >
              Refer and earn
            </Menu.Item>
          </Can>
        )}

        <Menu.Item
          onClick={() =>
            setColorScheme(computedColorScheme === "light" ? "dark" : "light")
          }
          leftSection={
            <ThemeIcon variant="transparent" color="dark" size="xs">
              {computedColorScheme === "light" ? <IconMoon /> : <IconSun />}
            </ThemeIcon>
          }
        >
          {computedColorScheme === "light" ? "Dark" : "Light"} mode
        </Menu.Item>
        <Menu
          trigger="hover"
          openDelay={100}
          closeDelay={200}
          position="right-start"
          offset={4}
        >
          <Menu.Target>
            <Menu.Item
              leftSection={
                <ThemeIcon variant="transparent" color="dark" size="xs">
                  <IconHelp />
                </ThemeIcon>
              }
            >
              Help & support
            </Menu.Item>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              component="a"
              href={chatSupportLink}
              referrerPolicy="no-referrer"
              target="_blank"
            >
              Livechat support
            </Menu.Item>
            <Menu.Item component="a" href={`mailto:${supportEmail}`}>
              Email support
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <Menu.Divider />
        <Menu.Item
          c="red.4"
          leftSection={
            <ThemeIcon variant="transparent" size="xs" c="red.4">
              <IconLogout />
            </ThemeIcon>
          }
          onClick={signOut}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
      <ReferralModal
        opened={referralModalOpened}
        onClose={closeReferralModal}
      />
    </Menu>
  );
}
